import { FC, useEffect, useState } from "react";
import styles from "./review-table.module.scss";

import { TableRowActions } from "../../../../ui/tableRowActions/tableRowActions";
import { useParams } from "react-router-dom";
import { useDeleteReviewMutation, useGetAllReviewsQuery } from "./reviewApi";
import { Button } from "../../../../ui/button/Button";
import { PreloaderBar } from "../../../../ui/preloaderBar/PreloaderBar";
import { Snackbar } from "../../../../ui/snackbar/Snackbar";
import { Confirm } from "../../../../ui/confirm/confirm";
import { IReviewList } from "./IReview";
import { AddReview } from "./addReview/addReview";
import { EditReview } from "./editReview/editReview";

type RowProps = Omit<IReviewList, "airbase_id" | "flight_work_id"> & {
  handler: (type: "edit" | "delete", id: number) => boolean;
};

const Row: FC<RowProps> = ({ id, airbase_name, multiplicity, handler }) => {
  return (
    <tr>
      <td>{airbase_name}</td>
      <td>{multiplicity}</td>
      <td>
        <TableRowActions id={id} handler={handler} />
      </td>
    </tr>
  );
};

export const ReviewTable: FC = () => {
  const { id } = useParams();

  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isUXOpen, setIsUXOpen] = useState(true);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [ID, setID] = useState<number | null>(null);

  const { data, error, isError, isFetching } = useGetAllReviewsQuery(
    Number(id)
  );

  const [
    deleteReview,
    {
      isError: isDeleteError,
      isLoading: isDeleteProcessing,
      isSuccess: isDeleteSuccess,
    },
  ] = useDeleteReviewMutation();

  const create = () => {
    setIsAddOpen(true);
  };

  const rowHandler = (type: "edit" | "delete", id: number) => {
    if (isConfirmOpen) return false;
    setIsUXOpen(true);
    setID(id);
    switch (type) {
      case "delete":
        setIsConfirmOpen(true);
        break;

      case "edit":
        setIsEditOpen(true);
        break;

      default:
        break;
    }
    return true;
  };

  useEffect(() => {
    if (!isDeleteSuccess) return;

    setIsConfirmOpen(false);
    setID(null);
  }, [isDeleteSuccess]);

  return (
    <section className={styles.main}>
      <header className={styles.header}>
        <h2>Осмотр авиаотделений</h2>
        <div className={styles.actions}>
          <Button onClick={create}>Добавить</Button>
        </div>
      </header>
      <table className={styles.table}>
        <thead>
          <tr>
            <th>Авиаотделение</th>
            <th>Фактическая кратность</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {data &&
            data.map((row) => (
              <Row
                key={row.id}
                id={row.id}
                airbase_name={row.airbase_name}
                multiplicity={row.multiplicity}
                handler={rowHandler}
              />
            ))}
        </tbody>
      </table>
      {isFetching && <PreloaderBar />}
      {isAddOpen && Number(id) && (
        <AddReview
          close={() => setIsAddOpen(false)}
          flight_work_id={Number(id)}
        />
      )}
      {isEditOpen && (
        <EditReview
          close={() => setIsEditOpen(false)}
          flight_work_id={Number(id)}
          review_id={Number(ID)}
        />
      )}
      {isError && "status" in error && error.status !== 404 && isUXOpen && (
        <Snackbar
          className="error"
          isOpen={isError}
          onClose={() => setIsUXOpen(false)}
        >
          Ошибка загрузки данных. Попробуйте позже
        </Snackbar>
      )}
      {isConfirmOpen && ID && (
        <Confirm
          confirm={() => deleteReview(ID)}
          reject={() => {
            setIsConfirmOpen(false);
            setID(null);
          }}
          text="Вы действительно хотите удалить запись из системы?"
          isProcessing={isDeleteProcessing}
        />
      )}
      {isDeleteSuccess && isUXOpen && (
        <Snackbar
          className="success"
          isOpen={isDeleteSuccess}
          onClose={() => setIsUXOpen(false)}
        >
          Запись удалена
        </Snackbar>
      )}
      {isDeleteError && isUXOpen && (
        <Snackbar
          className="error"
          isOpen={isDeleteError}
          onClose={() => setIsUXOpen(false)}
        >
          Ошибка удаления. Попробуйте позже
        </Snackbar>
      )}
    </section>
  );
};
